import React, { useState } from 'react'
import { ArtifactPluginProps } from './ArtifactPluginRegistry'
import { KycHeader } from './kyc/KycHeader'
import { KycPersonalInfo } from './kyc/KycPersonalInfo'
import { KycContactInfo } from './kyc/KycContactInfo'
import { KycIdentityDocuments } from './kyc/KycIdentityDocuments'
import { KycVaultDocuments } from './kyc/KycVaultDocuments'
import { Card, CardContent } from '../ui/card'
import { AlertCircle } from 'lucide-react'
import { Alert, AlertDescription } from '../ui/alert'
import { RawJsonModal } from './common/RawJsonModal'

interface VaultDocument {
  vault_id: string
  document_id: string
  document_title: string
  document_filename: string
  document_mime_type: string
  document_signature: string
}

interface KycData {
  request_id?: string
  created_date?: string
  personal_information?: {
    first_name: string
    middle_name?: string
    last_name: string
    alias?: string
    gender: string
    date_of_birth: string
    place_of_birth: {
      city: string
      state_or_province?: string
      country: string
    }
    nationality: string
  }
  contact_information?: {
    email?: string
    phone_number?: string
    alternate_phone_number?: string
    address: {
      street?: string
      apartment?: string
      city?: string
      state?: string
      postal_code?: string
      country?: string
    }
  }
  identity_documents?: Array<{
    document_type: string
    document_number: string
    issue_date: string
    expiry_date?: string
    issuing_country: string
    issuing_authority?: string
    documents?: any
    document_images?: {
      front?: string
      back?: string
    }
  }>
  vault_documents?: VaultDocument[]
}

/**
 * KYC Artifact Plugin
 * Displays KYC verification data in a structured, user-friendly format
 */
export const KycArtifactPlugin: React.FC<ArtifactPluginProps> = ({ transaction, onBack }) => {
  const [showRawJson, setShowRawJson] = useState(false)
  const kycData = transaction.data as KycData

  // Validate that we have KYC data
  if (!kycData || typeof kycData !== 'object') {
    return (
      <div className="space-y-3 animate-in fade-in duration-500">
        <KycHeader
          transactionId={transaction.transactionId}
          artifactId={transaction.artifactId}
          insertedAt={transaction.insertedAt}
          onBack={onBack}
          onViewRawJson={() => setShowRawJson(true)}
        />
        
        <Card className="border-destructive">
          <CardContent className="pt-4">
            <div className="text-center py-8">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
              <h3 className="font-medium mb-2">Invalid KYC Data</h3>
              <p className="text-sm text-muted-foreground">
                The transaction data does not contain valid KYC information.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-3 animate-in fade-in duration-500">
      {/* Header */}
      <KycHeader
        transactionId={transaction.transactionId}
        artifactId={transaction.artifactId}
        insertedAt={transaction.insertedAt}
        onBack={onBack}
        onViewRawJson={() => setShowRawJson(true)}
      />

      {/* Request ID if available */}
      {kycData.request_id && (
        <div className="flex items-center gap-4 text-sm p-2 bg-muted rounded">
          <span><span className="font-medium">Request ID:</span> {kycData.request_id}</span>
          {kycData.created_date && (
            <span><span className="font-medium">Created:</span> {kycData.created_date}</span>
          )}
        </div>
      )}

      {/* Personal Information */}
      {kycData.personal_information && (
        <KycPersonalInfo personalInfo={kycData.personal_information} />
      )}

      {/* Contact Information */}
      {kycData.contact_information && (
        <KycContactInfo contactInfo={kycData.contact_information} />
      )}

      {/* Identity Documents */}
      {kycData.identity_documents && kycData.identity_documents.length > 0 && (
        <KycIdentityDocuments documents={kycData.identity_documents} />
      )}

      {/* Vault Documents */}
      {kycData.vault_documents && kycData.vault_documents.length > 0 && (
        <KycVaultDocuments documents={kycData.vault_documents} />
      )}

      {/* Warning if no data sections available */}
      {!kycData.personal_information &&
       !kycData.contact_information &&
       (!kycData.identity_documents || kycData.identity_documents.length === 0) &&
       (!kycData.vault_documents || kycData.vault_documents.length === 0) && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground p-3 bg-muted rounded">
          <AlertCircle className="h-4 w-4" />
          <span>This KYC record does not contain personal information, contact information, identity documents, or vault documents.</span>
        </div>
      )}

      {/* Raw JSON Modal */}
      <RawJsonModal 
        transaction={transaction}
        open={showRawJson}
        onOpenChange={setShowRawJson}
      />
    </div>
  )
}
